// BroadcastingChannelDashboard.tsx — tableau de bord (aperçu, stats, programme, audience, actions, assistant)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import { format } from 'date-fns';
import { fr }     from 'date-fns/locale';
import styles from './BroadcastingChannelDashboard.module.scss';

import Box            from '../../widget/windows/Box';
import { ACTIVATE_PANEL_EVENT } from '../../widget/navigation/tab';
import { requestMediaCreation } from '../mediaManagment/MediaList';
import Icon           from '../../widget/images/Icon';
import MonitorVideoPlayer from '../../widget/players/MonitorVideoPlayer';
import ClonedVideoPlayer  from '../../widget/players/ClonedVideoPlayer';
import OpenStreetMapV2      from '../../widget/dataDisplay/maps/OpenStreetMapV2';
import ExtendablePanel    from '../../widget/windows/ExtendablePanel';
import HlsAudioPanel      from '../liveStreamManagment/HlsAudioPanel';
import SourceSelector     from './SourceSelector';
import SendChannelUrlModal from './SendChannelUrlModal';
import { RefreshCw }      from 'lucide-react';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';

import { useTypedSelector } from '../../../store/useTypeSelector';
import { RootState }        from '../../../store/generalReducer';
import {
  EnrichedBroadcastingChannel,
  StreamSource,
  TaggedEnrichedBroadcastedMedia,
} from '../../../dataTypes/broadcastingManagment/types';
import { Coordinate }        from '../../../dataTypes/geolocationManagment/types';
import { AudioSourceRegistry } from '../../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry';
import { ProgramVideoItem }    from '../../../dataTypes/broadcastingManagment/types';
import { formatDurationInSeconds } from '../../../dataTypes/timeManagment/utils';
import { getVideoAudioTracks, VideoAudioTrack } from '../../../backendServices/mediaManagment/api_video';

import {
  getLogoImageUrl,
  getPublicWatchingUrl,
  getProgramMetadata,
  getThumbnailUrl,
  getCurrentStreamMetadata,
  getXtreamConnectionLocations,
} from '../../../backendServices/broadcastingManagment/api_broadcastingChannel';


// Rend un QRCodeCanvas dans un div off-DOM à la taille cible exacte,
// sans jamais afficher ni scaler un canvas déjà rasterisé.
function renderQRAtSize(url: string, size: number): Promise<HTMLCanvasElement | null> {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    host.style.cssText = 'position:fixed;top:-9999px;left:-9999px;visibility:hidden;pointer-events:none;';
    document.body.appendChild(host);
    let src: HTMLCanvasElement | null = null;
    const root = createRoot(host);
    root.render(React.createElement(QRCodeCanvas, {
      ref: (el: HTMLCanvasElement | null) => { src = el; },
      value: url,
      size,
      bgColor: '#ffffff',
      fgColor: '#000000',
    }));
    requestAnimationFrame(() => requestAnimationFrame(() => {
      let snap: HTMLCanvasElement | null = null;
      if (src) {
        snap = document.createElement('canvas');
        snap.width = size; snap.height = size;
        const sc = snap.getContext('2d')!;
        sc.imageSmoothingEnabled = false;
        sc.drawImage(src, 0, 0);
        // QRCodeCanvas uses fractional fillRect when size/numModules isn't integer,
        // producing 1-pixel anti-aliased edges. Threshold to pure black/white.
        const img = sc.getImageData(0, 0, size, size);
        for (let i = 0; i < img.data.length; i += 4) {
          const v = (img.data[i] + img.data[i + 1] + img.data[i + 2]) / 3 < 128 ? 0 : 255;
          img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
          img.data[i + 3] = 255;
        }
        sc.putImageData(img, 0, 0);
      }
      root.unmount();
      document.body.removeChild(host);
      resolve(snap);
    }));
  });
}



// ── SVG icons ─────────────────────────────────────────────────────────────────
const Ico = {
  Eye:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  Users:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Monitor: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
  Signal:  () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M1 6l10.5 6L22 6"/><path d="M1 6v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6"/></svg>,
  Cal:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
  Clock:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  Repeat:  () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>,
  Share:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
  Star:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
  Play:    () => <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><polygon points="5 3 19 12 5 21 5 3"/></svg>,
  Link:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  Lock:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>,
  Grid:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>,
  Img:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
  ExtLink: () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  MapPin:  () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  Down:    () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11"><polyline points="6 9 12 15 18 9"/></svg>,
  QR:      () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="5" y="5" width="3" height="3"/><rect x="16" y="5" width="3" height="3"/><rect x="5" y="16" width="3" height="3"/><line x1="14" y1="14" x2="14" y2="14"/><line x1="17" y1="14" x2="17" y2="14"/><line x1="20" y1="14" x2="20" y2="14"/><line x1="14" y1="17" x2="14" y2="17"/><line x1="17" y1="17" x2="20" y2="17"/><line x1="20" y1="20" x2="20" y2="20"/><line x1="17" y1="20" x2="17" y2="20"/><line x1="14" y1="20" x2="14" y2="20"/></svg>,
  Check:   () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="10" height="10"><polyline points="20 6 9 17 4 12"/></svg>,
  YT:      () => <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31 31 0 0 0 0 12a31 31 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31 31 0 0 0 24 12a31 31 0 0 0-.5-5.8zM9.7 15.5V8.5l6.3 3.5-6.3 3.5z"/></svg>,
  Tag:     () => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>,
};

// ── Helpers programme ─────────────────────────────────────────────────────────
const JINGLE_FILTER = 'Jingle 2024';

function formatDayLabel(dateString: string): string {
  try { return format(new Date(dateString), 'EEEE d MMMM yyyy', { locale: fr }); }
  catch { return ''; }
}
function formatTime(dateString: string): string {
  try { return format(new Date(dateString), 'HH:mm'); }
  catch { return '--:--'; }
}
function truncateTitle(title: string, maxLength = 15): string {
  return title.length > maxLength ? `${title.slice(0, maxLength)}...` : title;
}

// ── Sub-components ────────────────────────────────────────────────────────────

const StatCard = ({ icon, label, value, accent, accent2, }: 
{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  accent: string;
  accent2?: string; // couleur du cercle extérieur
}) => (
  <div className={styles.statCard}>
    <div className={styles.statIconOuter} style={{ borderColor: accent2 ?? accent }}>
      <div className={styles.statIcon} style={{ background: accent }}>{icon}</div>
    </div>
    <div className={styles.statBody}>
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statValue}>{value}</div>
    </div>
  </div>
);
/*
const StatCard = ({ icon, label, value, accent, accent2, }: 
{ icon: React.ReactNode; 
  label: string; 
  value: React.ReactNode; 
  accent: string;
  accent2?: string;   }) => (
  <div className={styles.statCard} style={{ borderLeftColor: accent }}>
    <div className={styles.statIcon} style={{ color: accent, background: `${accent}22` }}>{icon}</div>
    <div className={styles.statBody}>
      <div className={styles.statLabel}>{label}</div>
      <div className={styles.statValue}>{value}</div>
    </div>
  </div>
);
*/

const SetupStep = ({ label, done }: { label: string; done: boolean }) => (
  <div className={styles.setupStep}>
    <span className={classNames(styles.setupDot, done ? styles.setupDone : styles.setupTodo)}>
      {done && <Ico.Check />}
    </span>
    <span className={classNames(styles.setupLabel, done && styles.setupLabelDone)}>{label}</span>
    <span className={classNames(styles.setupStatus, done ? styles.setupStatusDone : styles.setupStatusTodo)}>
      {done ? 'Terminé' : 'À faire'}
    </span>
  </div>
);

// ── Props ─────────────────────────────────────────────────────────────────────
export interface BroadcastingChannelDashboardProps {
  broadcastingChannelToEdit? : EnrichedBroadcastingChannel;
  creationMode?              : boolean;
  streamSource?              : StreamSource;
  defaultStreamSource?       : StreamSource;
  onStreamSourceChange?      : (s: StreamSource) => void;
  onDefaultStreamSourceChange?: (s: StreamSource) => void;
  isActive?                  : boolean;
  hlsVolume?                 : number;
  hlsMuted?                  : boolean;
  onHlsVolumeChange?         : (v: number) => void;
  onHlsMutedChange?          : (m: boolean) => void;
  hlsMonitorStream           : MediaStream | null;
  hlsAudioRegistry?          : AudioSourceRegistry;
  hlsAudioSourceId?          : string;
  onHlsReset?                : () => void;
  taggedBroadcastedMedias?   : TaggedEnrichedBroadcastedMedia[];
  // état actif/portail transmis depuis le parent (géré par BroadcastingChannelConfig)
  active?                    : boolean;
  channelStatus?             : 'active' | 'restricted' | 'activating' | 'activating_restricted' | 'inactive' | 'deactivating' | 'deactivating_restricted' | 'error' | 'recovering';
  xtreamViewerCount?         : number;
  publicPortal?              : boolean;
  onActiveToggle?            : () => void | Promise<boolean | void>;
  onPortalChange?            : (v: boolean) => void;
  metricsEnabled?            : boolean;
  metricsReachable?          : boolean;
  onMetricsToggle?           : (v: boolean) => void;
  // mots-clés enrichis transmis depuis le parent
  keywords?                  : { id: number; word: string }[];
  // catégorie et accès transmis depuis le parent
  categoryLabel?             : string;
  accessLabel?               : string;
  // créateur à chaîne unique : « Créer une chaîne » vit ici, faute de liste
  onCreateChannel?           : () => void;
  // « Actions rapides » : bascule vers un onglet de la fiche (0 Tableau de bord … 4 Restream)
  onNavigateTab?             : (index: number, focus?: 'youtube') => void;
}

// ── Composant ─────────────────────────────────────────────────────────────────
export const BroadcastingChannelDashboard = ({
  broadcastingChannelToEdit,
  creationMode,
  streamSource:          streamSourceProp,
  defaultStreamSource:   defaultStreamSourceProp,
  onStreamSourceChange,
  onDefaultStreamSourceChange,
  isActive = true,
  hlsVolume, hlsMuted, onHlsVolumeChange, onHlsMutedChange,
  hlsMonitorStream, hlsAudioRegistry, hlsAudioSourceId, onHlsReset,
  taggedBroadcastedMedias = [],
  active        = false,
  channelStatus = 'inactive' as const,
  xtreamViewerCount,
  publicPortal  = false,
  onActiveToggle,
  onPortalChange,
  metricsEnabled   = false,
  metricsReachable = false,
  onMetricsToggle,
  keywords      = [],
  categoryLabel = '',
  accessLabel   = '',
  onCreateChannel,
  onNavigateTab,
}: BroadcastingChannelDashboardProps) => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);

  const name      = broadcastingChannelToEdit?.name      ?? '';
  const logoSrc   = broadcastingChannelToEdit ? getLogoImageUrl(broadcastingChannelToEdit) : '';
  const publicUrl = broadcastingChannelToEdit ? getPublicWatchingUrl(broadcastingChannelToEdit) : '';
  const tpChannels = broadcastingChannelToEdit?.thirdPartyStreamingChannels ?? [];
  // Chaîne créée et pilotée par un autre système (BroadcastingChannel.ephemeral) — simple
  // rappel visuel ici, pas un toggle : rien à confirmer, rien à changer depuis ce badge.
  const ephemeral  = broadcastingChannelToEdit?.ephemeral ?? false;

  const streamSource        = streamSourceProp        ?? 'video';
  const defaultStreamSource = defaultStreamSourceProp ?? 'video';

  const [connections,     setConnections]     = useState(0);
  const [coords,          setCoords]          = useState<Coordinate[]>([]);
  const [xtreamConnections, setXtreamConnections] = useState(0);
  const [xtreamCoords,      setXtreamCoords]      = useState<Coordinate[]>([]);
  const [tvWindowOpen,    setTvWindowOpen]    = useState(false);
  useEffect(() => {
    if (!tvWindowOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setTvWindowOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [tvWindowOpen]);
  const [confirmPortalOpen, setConfirmPortalOpen] = useState(false);
  const [shareMenuOpen,   setShareMenuOpen]   = useState(false);
  const [shareModal,      setShareModal]      = useState<'mail'|'qr-only'|'qr-image'|null>(null);
  const [qrBgImageUrl,    setQrBgImageUrl]    = useState<string|null>(null);
  const [qrPos,           setQrPos]           = useState({ x: 20, y: 20 });
  const [qrSize,          setQrSize]          = useState(150);
  const [,         setQrScale]         = useState<number | null>(null); // naturalWidth / containerWidth

  const shareMenuRef    = useRef<HTMLDivElement>(null);
  const qrOnlyRef       = useRef<HTMLCanvasElement>(null);
  const bgImgRef        = useRef<HTMLImageElement>(null);
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const dragRef         = useRef({ isDragging: false, dx: 0, dy: 0, isResizing: false, resizeStartX: 0, resizeStartSize: 0 });

  // Case de la question d'activation : afficher aussi la chaîne sur le portail public.
  // Cochée par défaut, remise à zéro à chaque ouverture de la question.
  const [publishOnPortalWithActivation, setPublishOnPortalWithActivation] = useState(true);
  const [confirmToggleOpen, setConfirmToggleOpen] = useState(false);
  useEffect(() => { if (confirmToggleOpen) setPublishOnPortalWithActivation(true); }, [confirmToggleOpen]);

  // ── Bascule active/inactive : intention et garde anti double-clic ───────────
  // L'intention se lit sur `channelStatus` (l'intention enregistrée en base), JAMAIS sur
  // `active` : ce booléen vient de getActive() côté backend, qui renvoie l'état du container
  // Docker et non le drapeau en base. Une chaîne restée à active=1 en base alors que son
  // container, sa route HAProxy et ses paths MediaMTX ont été supprimés arrive donc ici avec
  // channelStatus='activating' ET active=false — la modale proposait « Activer » là où la
  // seule action utile est de l'arrêter pour de bon.
  const toggleWillDeactivate = channelStatus !== 'inactive';

  // Garde anti double-soumission, volontairement BORNÉE DANS LE TEMPS. Elle remplace un
  // disabled={channelStatus === 'activating' || channelStatus === 'deactivating'} qui rendait
  // le bouton de confirmation définitivement inerte dès qu'une chaîne restait coincée dans un
  // état transitoire : l'opérateur n'avait alors plus aucun moyen de l'arrêter depuis
  // l'interface. Ne jamais rebrancher cette condition sur channelStatus.
  // Elle retient le SENS de la dernière soumission : ne bloque que la même action redemandée,
  // jamais l'action inverse (activer puis aussitôt « Arrêt forcé » doit rester possible).
  const [toggleBusy, setToggleBusy] = useState<'activate' | 'deactivate' | null>(null);
  const toggleBusyTimerRef = useRef<number | null>(null);
  const TOGGLE_BUSY_MS = 10000;

  useEffect(() => () => {
    if (toggleBusyTimerRef.current !== null) { window.clearTimeout(toggleBusyTimerRef.current); }
  }, []);

  const [programVideos,        setProgramVideos]        = useState<ProgramVideoItem[]>([]);
  // Bulle d'explication « Jouer en boucle » au survol d'une tuile du programme. Rendue par
  // portail en position fixe : la liste défile (overflow-y), elle rognerait une bulle interne.
  const [programTip,           setProgramTip]           = useState<{ text: string; left: number; top: number; below: boolean } | null>(null);
  const [programLoading,       setProgramLoading]       = useState(false);
  const [currentBroadcastedId, setCurrentBroadcastedId] = useState<number | null>(null);
  // Départ RÉEL de la vidéo à l'antenne, en horloge du navigateur : maintenant − elapsed_seconds
  // de schedule/current. La jauge partait de scheduled_time, l'heure PRÉVUE — or une vidéo
  // enchaînée démarre dès la fin réelle de la précédente, souvent 1 à 3 s avant son heure
  // (rafale de démarrage de 0,5 s, durées arrondies à la seconde) : jauge en retard d'autant
  // (73 départs en avance relevés sur sept.-oct. 2026). elapsed_seconds part de
  // actual_start_time, latence HLS comprise : la jauge suit l'image vue par le spectateur.
  // Le calcul par écoulé (et non par l'heure absolue) ignore aussi le décalage d'horloge
  // entre le poste et le serveur. Redémarre à chaque tour d'une boucle.
  const [liveStartMs,          setLiveStartMs]          = useState<number | null>(null);

  // Pistes audio des médias affichés dans le programme du tableau de bord, par mediaId —
  // même principe que BroadcastedMediaListV2 (fetchedTrackIdsRef évite de refetcher un
  // média déjà interrogé). Affichage SEUL ici : cet onglet n'édite jamais le programme.
  const [audioTracksCache, setAudioTracksCache] = useState<Record<number, VideoAudioTrack[]>>({});
  const fetchedTrackIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    const mediaIds = new Set(programVideos.map(v => v.media_id).filter(id => id > 0));
    mediaIds.forEach(id => {
      if (fetchedTrackIdsRef.current.has(id)) return;
      fetchedTrackIdsRef.current.add(id);
      getVideoAudioTracks(id).then(response => {
        setAudioTracksCache(prev => ({ ...prev, [id]: response.code === 1 ? response.payload : [] }));
      });
    });
  }, [programVideos]);

  // Horloge locale pour la jauge de progression de la vidéo en cours — le
  // polling du programme (PROGRAM_POLLING_INTERVAL, 15 s) est trop grossier
  // pour une jauge qui doit avancer visiblement. N'existe que pour recalculer
  // le rendu chaque seconde ; la progression elle-même se déduit de
  // scheduled_time + duration_seconds, jamais de ce compteur directement.
  const [progressClock, setProgressClock] = useState<Date>(() => new Date());
  useEffect(() => {
    if (!isActive) return;
    const id = setInterval(() => setProgressClock(new Date()), 1000);
    return () => clearInterval(id);
  }, [isActive]);

  // ── Fermeture dropdowns au clic extérieur ────────────────────────────────────
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target as Node)) setShareMenuOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // ── Programme ──────────────────────────────────────────────────────────────
  // Cadence alignée sur celle de l'onglet Programmation (BroadcastedMediaListV2).
  const PROGRAM_POLLING_INTERVAL = 15000;

  // Chaîne dont le programme est déjà affiché : sert à n'afficher « Chargement... »
  // que lorsqu'il n'y a encore rien à l'écran pour cette chaîne.
  const loadedProgramRefRef  = useRef<string | null>(null);
  const programPollRef       = useRef<NodeJS.Timeout | null>(null);

  // Numéro de la dernière requête LANCÉE, pour ignorer une réponse tardive d'un appel plus
  // ancien qui arriverait après un appel plus récent — sans ça, un poll de fond encore en
  // vol au moment d'un retour sur l'onglet (isActive) pouvait répondre APRÈS le fetch frais
  // déclenché par ce retour, et écraser silencieusement l'affichage avec des données
  // périmées : la requête repartait bien (visible dans l'onglet Réseau) mais le résultat
  // affiché restait celui d'avant.
  const fetchSeqRef = useRef(0);

  // ⚠️ Le paramètre est une ADRESSE (la référence publique), pas une identité :
  // les deux relevés composent une URL sur cast.kleonpro.tv, et l'uuid n'y route
  // plus — il rendait 404, que l'appel voyait comme une simple « Network Error ».
  const fetchProgram = useCallback(async (publicRef: string, options?: { silent?: boolean }) => {
    if (!publicRef) return;
    const silent = options?.silent === true;
    const seq = ++fetchSeqRef.current;
    if (!silent) setProgramLoading(true);
    try {
      const [programData, currentData] = await Promise.all([
        getProgramMetadata(publicRef, { todayOnly: true }),
        getCurrentStreamMetadata(publicRef),
      ]);
      // Une requête plus récente a déjà été lancée entre-temps (et appliquera, ou a déjà
      // appliqué, son propre résultat) : celle-ci est obsolète, on ne touche à rien.
      if (seq !== fetchSeqRef.current) return;
      // getProgramMetadata renvoie null quand la requête échoue (elle n'émet pas
      // d'exception) : lors d'un rafraîchissement silencieux, on conserve alors le
      // programme déjà affiché plutôt que de le vider sur un incident réseau passager.
      // Un programme réellement vide, lui, renvoie bien { videos: [] }.
      if (programData !== null) {
        setProgramVideos(programData.videos ?? []);
        loadedProgramRefRef.current = publicRef;
      } else if (!silent) {
        setProgramVideos([]);
      }
      // Même protection que ci-dessus : getCurrentStreamMetadata renvoie null quand la
      // requête échoue, ce qui est indiscernable d'une vraie mire si on écrase l'état.
      // Sur un rafraîchissement silencieux (toutes les 15 s), un seul incident réseau
      // faisait disparaître le badge « EN COURS » et retombait la vignette sur « Mire ».
      // Une absence réelle de vidéo, elle, renvoie bien { status: 'idle' } sans id.
      if (currentData !== null) {
        setCurrentBroadcastedId(currentData.broadcasted_media_id ?? null);
        setLiveStartMs(currentData.status === 'playing' && typeof currentData.elapsed_seconds === 'number'
          ? Date.now() - currentData.elapsed_seconds * 1000
          : null);
      } else if (!silent) {
        setCurrentBroadcastedId(null);
        setLiveStartMs(null);
      }
    } catch {
      if (seq !== fetchSeqRef.current) return;
      if (!silent) { setProgramVideos([]); setCurrentBroadcastedId(null); setLiveStartMs(null); }
    } finally {
      if (seq === fetchSeqRef.current && !silent) setProgramLoading(false);
    }
  }, []);

  // Rafraîchissement automatique du programme, comme l'onglet Programmation :
  // à l'arrivée sur l'onglet puis toutes les PROGRAM_POLLING_INTERVAL ms.
  // Le tableau de bord reste monté quand on le quitte (keepMounted) : sans le
  // déclencheur sur `isActive`, un retour sur l'onglet réaffichait des données figées.
  //
  // `channelStatus` est dans les dépendances pour la même raison : passer par
  // 'activating'/'deactivating' change le planning côté microservice (nouveau container,
  // fichier de planning purgé/rechargé — cf. purgeOrphanedScheduleEntries côté admin-api),
  // sans que `publicRef` ni `isActive` (visibilité de l'onglet) ne changent. Sans ce
  // déclencheur, démarrer/arrêter la chaîne depuis ce même tableau de bord laissait
  // l'ancien programme affiché jusqu'au prochain tick du polling (jusqu'à 15 s), voire
  // figé si l'utilisateur ne restait pas sur l'onglet assez longtemps pour un tick.
  useEffect(() => {
    const publicRef = broadcastingChannelToEdit?.publicRef;
    if (!publicRef || !isActive) return;

    fetchProgram(publicRef, { silent: loadedProgramRefRef.current === publicRef });

    programPollRef.current = setInterval(
      () => fetchProgram(publicRef, { silent: true }),
      PROGRAM_POLLING_INTERVAL,
    );
    return () => { if (programPollRef.current) clearInterval(programPollRef.current); };
  }, [broadcastingChannelToEdit?.publicRef, isActive, channelStatus, fetchProgram]);

  // ── Audience ───────────────────────────────────────────────────────────────
  // La carte ne montre que les spectateurs du relevé Xtream ci-dessous. L'ancien relevé HLS
  // (CustomerFrontEvent) ne reçoit plus rien depuis le 2026-08-07 : les compteurs HLS restent à 0.
  const [audienceUpdatedAt, setAudienceUpdatedAt] = useState<Date | null>(null);
  // Changer de chaîne ou d'état : on repart d'une carte vide, sans points de la fiche précédente.
  useEffect(() => {
    setCoords([]); setConnections(0); setXtreamCoords([]); setXtreamConnections(0);
  }, [channelStatus, broadcastingChannelToEdit?.uuid]);
  const refreshAudience = () => { pollXtreamAudienceRef.current(); };
  useEffect(() => {
    // Onglet resté en arrière-plan : on rattrape tout de suite au retour (comme les stats V2).
    const onReturn = () => { if (!document.hidden) refreshAudience(); };
    document.addEventListener('visibilitychange', onReturn);
    return () => document.removeEventListener('visibilitychange', onReturn);
  }, []);

  // ── Audience Xtream (IPTV) ───────────────────────────────────────────────────
  const xtreamIntRef = useRef<NodeJS.Timeout|null>(null);
  const broadcastingChannelIdRef = useRef<number | undefined>(undefined);
  broadcastingChannelIdRef.current = broadcastingChannelToEdit?.id;
  const pollXtreamAudience = async () => {
    if (!broadcastingChannelToEdit?.id || channelStatus !== 'active') {
      setXtreamConnections(0); setXtreamCoords([]); return;
    }
    const forId = broadcastingChannelToEdit.id;
    try {
      const r = await getXtreamConnectionLocations(forId);
      if (broadcastingChannelIdRef.current !== forId) return;
      setXtreamConnections(r.code===1?r.payload.count:0);
      setXtreamCoords(r.code===1?r.payload.locations.map(loc => ({
        lat   : loc.lat,
        lng   : loc.lon,
        info1 : [loc.city, loc.country].filter(Boolean).join(', ') || loc.ipAddress,
        info2 : `IPTV — ${loc.username}`,
        color : '#3b82f6',
      })):[]);
      setAudienceUpdatedAt(new Date());
    }
    catch { setXtreamConnections(0); setXtreamCoords([]); }
  };
  const pollXtreamAudienceRef = useRef<() => void>(() => {});
  pollXtreamAudienceRef.current = pollXtreamAudience;
  useEffect(() => {
    pollXtreamAudience();
    xtreamIntRef.current = setInterval(() => { if (!document.hidden) pollXtreamAudience(); }, 20000);
    return () => { if(xtreamIntRef.current) clearInterval(xtreamIntRef.current); };
  }, [broadcastingChannelToEdit?.id, channelStatus]);

  const totalConnections = connections + xtreamConnections;
  const mapCoordinates   = [...coords, ...xtreamCoords];

  // ── Partage ────────────────────────────────────────────────────────────────
  const onShareUrl = () => setShareMenuOpen(v => !v);

  const closeShareModal = useCallback(() => {
    setShareModal(null);
    if (qrBgImageUrl) { URL.revokeObjectURL(qrBgImageUrl); setQrBgImageUrl(null); }
    setQrPos({ x: 20, y: 20 });
    setQrSize(150);
  }, [qrBgImageUrl]);

  const openMailModal = () => {
    setShareMenuOpen(false);
    setShareModal('mail');
  };

  const downloadQrOnly = async () => {
    // Rendu direct à 1000 px — pas de scaling d'un canvas déjà rasterisé.
    const qr = await renderQRAtSize(publicUrl, 1000);
    if (!qr) return;
    const a = document.createElement('a');
    a.href = qr.toDataURL('image/png');
    a.download = 'qrcode.png';
    a.click();
  };

  const downloadQrInImage = async () => {
    const bgImg = bgImgRef.current;
    const cont  = imgContainerRef.current;
    if (!bgImg || !cont) return;

    // Taille cible dans l'image finale (pixels naturels de l'image de fond)
    const scaleX  = bgImg.naturalWidth  / cont.clientWidth;
    const scaleY  = bgImg.naturalHeight / cont.clientHeight;
    const targetW = Math.round(qrSize * scaleX);
    const targetH = Math.round(qrSize * scaleY);

    // QR rendu exactement à la taille de sortie — aucun upscaling
    const qr = await renderQRAtSize(publicUrl, Math.max(targetW, targetH));

    const out = document.createElement('canvas');
    out.width  = bgImg.naturalWidth;
    out.height = bgImg.naturalHeight;
    const ctx  = out.getContext('2d')!;
    ctx.drawImage(bgImg, 0, 0);
    if (qr) {
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(qr, 0, 0, qr.width, qr.height,
        Math.round(qrPos.x * scaleX), Math.round(qrPos.y * scaleY),
        targetW, targetH);
      ctx.imageSmoothingEnabled = true;
    }

    const a = document.createElement('a');
    a.href = out.toDataURL('image/png');
    a.download = 'channel-qr-image.png';
    a.click();
  };

  const onBgImageFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (qrBgImageUrl) URL.revokeObjectURL(qrBgImageUrl);
    setQrBgImageUrl(URL.createObjectURL(file));
    setQrPos({ x: 20, y: 20 });
    setQrScale(null);
    e.target.value = '';
  };

  const onBgImageLoad = () => {
    const img  = bgImgRef.current;
    const cont = imgContainerRef.current;
    if (img && cont && cont.clientWidth > 0) {
      setQrScale(img.naturalWidth / cont.clientWidth);
    }
  };

  const onQrMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const qrRect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    dragRef.current = { ...dragRef.current, isDragging: true, dx: e.clientX - qrRect.left, dy: e.clientY - qrRect.top };
  };

  const onResizeMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { ...dragRef.current, isResizing: true, resizeStartX: e.clientX, resizeStartSize: qrSize };
  };

  const onContainerMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (d.isResizing) {
      const newSize = Math.max(60, Math.min(400, d.resizeStartSize + (e.clientX - d.resizeStartX)));
      setQrSize(newSize);
      const cont = imgContainerRef.current;
      if (cont) setQrPos(prev => ({
        x: Math.min(prev.x, cont.clientWidth  - newSize),
        y: Math.min(prev.y, cont.clientHeight - newSize),
      }));
      return;
    }
    if (!d.isDragging) return;
    const rect = (imgContainerRef.current as HTMLDivElement).getBoundingClientRect();
    const x    = Math.max(0, Math.min(e.clientX - rect.left - d.dx, rect.width  - qrSize));
    const y    = Math.max(0, Math.min(e.clientY - rect.top  - d.dy, rect.height - qrSize));
    setQrPos({ x, y });
  };

  const onContainerMouseUp = () => {
    dragRef.current.isDragging = false;
    dragRef.current.isResizing = false;
  };

  // ── Dérivés ────────────────────────────────────────────────────────────────
  const activeRestreams = tpChannels.filter(c=>c.active).length;
  const currentMedia    = taggedBroadcastedMedias.find(m=>m.tag==='current');

  // Titre de la vidéo en cours. Les tags de taggedBroadcastedMedias ne sont rafraîchis
  // que par l'onglet Programmation (démonté quand on est sur le tableau de bord) : dès
  // que le programme a été chargé ici, on s'appuie sur les métadonnées rafraîchies en
  // continu, sinon la vignette « Vidéo en cours » reste figée.
  const programCurrentTitle = currentBroadcastedId !== null
    ? programVideos.find(v => v.broadcasted_media_id === currentBroadcastedId)?.media_name
    : undefined;
  // La playlist ne dit rien de l'antenne quand une autre source y est (2026-10-24) :
  // régie à l'antenne, schedule/current répond « idle » et le bloc affichait « Mire »
  // (Kleon TV, streamSource = webrtc). La source d'antenne passe donc en premier.
  const currentMediaLabel = channelStatus === 'inactive'
    ? 'Chaîne inactive'
    : streamSource === 'webrtc'
    ? 'Régie (direct)'
    : streamSource === 'rtmp'
    ? 'Flux RTMP (OBS…)'
    : loadedProgramRefRef.current !== null
    ? (programCurrentTitle ?? 'Mire')
    : (currentMedia?.media?.name ?? 'Mire');
  const kwLabel         = keywords.map(k => k.word).join(', ');
  const steps = [
    {label:'Nom de la chaîne',          done: name.trim().length>0},
    {label:'Première vidéo programmée', done: taggedBroadcastedMedias.length>0},
    {label:"Image d'attente",           done: !!(broadcastingChannelToEdit?.pendingImageFile)},
    {label:'YouTube connecté',          done: activeRestreams>0},
  ];
  const doneCount = steps.filter(s=>s.done).length;

  // « Ajouter une vidéo » : section Médias, fiche vidéo vierge (comme « Créer un média »).
  // « Actions rapides » : la page de destination s'ouvre en haut. Le défilement appartient à un
  // conteneur parent (body ou zone de contenu du menu) qui survit au changement d'onglet ou de
  // section ; on remet à zéro chaque ancêtre défilant, relevés AVANT que le Dashboard soit
  // démonté (« Ajouter une vidéo » change de section), puis une fois la destination rendue.
  const rootRef = useRef<HTMLDivElement>(null);
  const scrollPageToTop = () => {
    const scrollables: HTMLElement[] = [];
    for (let el = rootRef.current?.parentElement; el; el = el.parentElement) {
      if (el.scrollHeight > el.clientHeight) scrollables.push(el);
    }
    const reset = () => {
      scrollables.forEach(el => { el.scrollTop = 0; });
      window.scrollTo(0, 0);
    };
    reset();
    requestAnimationFrame(reset);
  };

  const openNewVideoSheet = () => {
    requestMediaCreation();
    window.dispatchEvent(new CustomEvent(ACTIVATE_PANEL_EVENT, { detail: { panelKey: 'mediaManagment' } }));
  };

  // ── Programme rendu ────────────────────────────────────────────────────────
  const renderProgram = () => {
    if (programLoading) return <div className={styles.scheduleList}><div className={styles.scheduleItem} style={{opacity:0.5}}>Chargement...</div></div>;
    const filtered = programVideos.filter(v=>v.media_name!==JINGLE_FILTER);
    if (!filtered.length) return <div className={styles.scheduleList}><div className={styles.scheduleItem} style={{opacity:0.5,justifyContent:'center'}}>Aucune émission programmée</div></div>;
    const elements: JSX.Element[] = [];
    let currentDay = '';

    // « Jouer en boucle » : le programme en boucle tourne indéfiniment, ceux qui le suivent ne
    // passeront pas tant que la boucle n'est pas désactivée (le microservice les retient). Le
    // drapeau vient du microservice ; à défaut (non redéployé), de la BDD.
    const isLoopVideo = (v: ProgramVideoItem) =>
      v.loop === true || taggedBroadcastedMedias.find(m => m.id === v.broadcasted_media_id)?.loop === true;
    const loopIndex   = filtered.findIndex(isLoopVideo);
    const loopName    = loopIndex !== -1 ? filtered[loopIndex].media_name : '';

    filtered.forEach((video, i) => {
      const dayStr = formatDayLabel(video.scheduled_time);
      if (currentDay !== dayStr) { currentDay = dayStr; elements.push(<div key={`day-${i}`} className={styles.dayLabel}>{currentDay}</div>); }
      const isLive = currentBroadcastedId !== null && video.broadcasted_media_id === currentBroadcastedId;
      // HH:MM:SS, comme dans l'onglet Programmation (formatDuration de
      // BroadcastedMediaListV2.tsx) — uniformisé, plus de minutes brutes.
      const durationLabel = video.duration_seconds > 0 ? formatDurationInSeconds(video.duration_seconds) : null;

      // Progression de la vidéo EN COURS uniquement — déduite de scheduled_time
      // (début de diffusion) + duration_seconds, pas d'un champ "position"
      // séparé qui n'existe pas côté API. Bornée à [0, duration] : une horloge
      // client en avance/retard sur le serveur, ou un item pas encore vraiment
      // démarré, ne doit pas faire déborder la jauge.
      let elapsedSeconds: number | null = null;
      let progressRatio = 0;
      if (isLive && video.duration_seconds > 0) {
        // Départ réel si connu, heure prévue à défaut (microservice muet sur l'écoulé).
        const startMs = liveStartMs ?? new Date(video.scheduled_time).getTime();
        if (!Number.isNaN(startMs)) {
          const sinceStart = Math.max(0, (progressClock.getTime() - startMs) / 1000);
          // En boucle : progression dans le tour en cours (le modulo couvre une relance
          // survenue depuis le dernier relevé, au plus 15 s plus tôt).
          elapsedSeconds = video.loop ? sinceStart % video.duration_seconds : Math.min(video.duration_seconds, sinceStart);
          progressRatio  = elapsedSeconds / video.duration_seconds;
        }
      }

      // Texte seul, jamais un menu : le tableau de bord n'édite pas le programme (cf.
      // BroadcastedMediaListV2 pour la version modifiable). Rien à afficher pour un média
      // sans ambiguïté (une seule piste, ou pistes pas encore chargées).
      const trackList     = audioTracksCache[video.media_id] ?? [];
      const bmForAudio     = taggedBroadcastedMedias.find(m => m.id === video.broadcasted_media_id);
      const audioTrackLabel = trackList.length > 1
        ? (trackList[bmForAudio?.audioTrackIndex ?? 0] ?? trackList[0]).label
        : null;

      const isLoop = i === loopIndex;
      const isHeld = loopIndex !== -1 && i > loopIndex;
      const tip    = isLoop
        ? "Joue en boucle : cette vidéo est relancée indéfiniment. Les programmes suivants ne seront pas diffusés tant que la boucle n'est pas désactivée (onglet Programmation)."
        : isHeld
          ? `Suspendu : « ${loopName} » joue en boucle avant ce programme. Il ne sera pas diffusé tant que la boucle n'est pas désactivée (onglet Programmation).`
          : null;

      elements.push(
        <div key={video.broadcasted_media_id}
             className={classNames(styles.scheduleItem, isLive && styles.scheduleItemLive, isHeld && styles.scheduleItemHeld)}
             onMouseEnter={tip ? (e) => {
               const r = e.currentTarget.getBoundingClientRect();
               // Trop près du haut de l'écran : sous la tuile plutôt qu'au-dessus.
               const below = r.top < 110;
               setProgramTip({ text: tip, left: r.left + r.width / 2, top: below ? r.bottom : r.top, below });
             } : undefined}
             onMouseLeave={tip ? () => setProgramTip(null) : undefined}>
          {/* La garde interroge ce que l'URL utilise, jamais son voisin : media_uuid
              est toujours là, mais son ancienne adresse sort en 404 depuis que les
              ressources sont rangées par créateur. Pas de repli dessus, donc. */}
          {video.media_publicRef && (
            <div className={styles.scheduleThumbWrap}>
              <img className={styles.scheduleThumb} src={getThumbnailUrl(video.media_publicRef)} alt={video.media_name} onError={(e)=>{ (e.target as HTMLImageElement).style.display='none'; }}/>
              {elapsedSeconds !== null && (
                <div className={styles.scheduleProgressTrackWrap}>
                  <div className={styles.scheduleProgressTrack}>
                    <div className={styles.scheduleProgressFill} style={{ width: `${progressRatio * 100}%` }}/>
                  </div>
                </div>
              )}
            </div>
          )}
          <div className={styles.scheduleBody}>
            <div className={styles.scheduleMeta}>
              <span className={styles.scheduleRange}>{formatTime(video.scheduled_time)}</span>
              {isLive && <span className={styles.liveBadge}>● EN COURS</span>}
              {isLoop && <span className={styles.loopBadge}><Ico.Repeat/>En boucle</span>}
              {isHeld && <span className={styles.heldBadge}>Suspendu</span>}
            </div>
            <div className={styles.scheduleTitle}>{video.media_name}</div>
            {durationLabel !== null && (
              <div className={styles.scheduleDuration}>
                <span className={styles.scheduleDurationIcon}><Ico.Clock/></span>{durationLabel}
              </div>
            )}
            {audioTrackLabel !== null && (
              <div className={styles.scheduleDuration}>{audioTrackLabel}</div>
            )}
            {elapsedSeconds !== null && (
              <div className={styles.scheduleProgressTime}>
                <span className={styles.scheduleDurationIcon}><Ico.Eye/></span>{formatDurationInSeconds(elapsedSeconds)}
              </div>
            )}
          </div>
        </div>
      );
    });
    return (
      <>
        <div className={styles.scheduleList} onScroll={() => setProgramTip(null)}>{elements}</div>
        {programTip && ReactDOM.createPortal(
          <div className={classNames(styles.scheduleTip, programTip.below && styles.scheduleTipBelow)} role="tooltip" style={{ left: programTip.left, top: programTip.top }}>
            {programTip.text}
          </div>,
          document.body,
        )}
      </>
    );
  };

  // ── JSX ────────────────────────────────────────────────────────────────────
  return (
    <div ref={rootRef} className={styles.root}>

      {/* modales */}
      {confirmToggleOpen && ReactDOM.createPortal(
        <div className={styles.modalOverlay}>
          <div className={styles.modalBox} style={{maxWidth:420}}>
            <div className={styles.modalHead}>
              <span className={styles.modalTitle}>{!toggleWillDeactivate 
			                                          ? 'Activation de la chaîne' 
													  : (channelStatus === 'activating' || channelStatus === 'activating_restricted' || channelStatus === 'error')
													       ? "Arrêt forcé de la chaine"
														   : "Arrêt de la chaine"
												  }</span>
              <button className={styles.modalClose} onClick={()=>setConfirmToggleOpen(false)}>×</button>
            </div>
            <p style={{margin:'12px 0 20px',fontSize:'0.9rem',lineHeight:'1.5'}}>
              {!toggleWillDeactivate
                ? 'Voulez-vous activer la chaîne ? Elle sera visible par le public (le lancement prend quelques secondes).'
                : (channelStatus === 'activating' || channelStatus === 'activating_restricted' || channelStatus === 'error')
                  ? <>Si l'activation de cette chaine n'est pas allée à son terme, vous avez la possibilité d'interrompre le processus.<br/><br/>Voulez-vous interrompre le processus ?</>
                  : (channelStatus === 'deactivating' || channelStatus === 'deactivating_restricted')
                    ? "Une désactivation est déjà en cours. Voulez-vous la relancer ? Utile si elle est restée bloquée : le démontage est rejoué de bout en bout."
                    : 'Voulez-vous désactiver la chaîne ? Elle ne sera plus accessible au public.'}
            </p>
            {/* Rien à proposer si la chaîne est déjà sur le portail. */}
            {!toggleWillDeactivate && !publicPortal &&
             <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 14, cursor: 'pointer' }}>
               <input type     = "checkbox"
                      checked  = {publishOnPortalWithActivation}
                      onChange = {(e) => setPublishOnPortalWithActivation(e.target.checked)} />
               Afficher aussi la chaîne sur le portail public
             </label>}
            <div className={styles.modalActions} style={{justifyContent:'flex-end'}}>
              <button className={classNames(styles.btn, styles.btnOutline)} onClick={()=>setConfirmToggleOpen(false)}>Non</button>
              <button
                className={classNames(styles.btn, styles.btnPrimary)}
                disabled={toggleBusy === (toggleWillDeactivate ? 'deactivate' : 'activate')}
                onClick={()=>{
                  setConfirmToggleOpen(false);
                  setToggleBusy(toggleWillDeactivate ? 'deactivate' : 'activate');
                  if (toggleBusyTimerRef.current !== null) { window.clearTimeout(toggleBusyTimerRef.current); }
                  toggleBusyTimerRef.current = window.setTimeout(() => setToggleBusy(null), TOGGLE_BUSY_MS);
                  // Le portail n'est touché que si l'activation est ACCEPTÉE (true) : un refus de
                  // quota laisserait sinon la chaîne visible sans être démarrée.
                  const wantsPortal = !toggleWillDeactivate && !publicPortal && publishOnPortalWithActivation;
                  void Promise.resolve(onActiveToggle?.()).then(accepted => { if (accepted === true && wantsPortal) onPortalChange?.(true); });
                }}>
                Oui
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {confirmPortalOpen && ReactDOM.createPortal(
        <div className={styles.modalOverlay}>
          <div className={styles.modalBox} style={{maxWidth:420}}>
            <div className={styles.modalHead}>
              <span className={styles.modalTitle}>{publicPortal ? 'Rendre la chaîne privée' : 'Rendre la chaîne publique'}</span>
              <button className={styles.modalClose} onClick={()=>setConfirmPortalOpen(false)}>×</button>
            </div>
            <p style={{margin:'12px 0 20px',fontSize:'0.9rem',lineHeight:'1.5'}}>
              {publicPortal
                ? 'Voulez-vous retirer la chaîne du portail public ? Elle ne sera plus visible dans le catalogue.'
                : 'Voulez-vous rendre la chaîne visible sur le portail public ? Elle apparaîtra dans le catalogue.'}
            </p>
            <div className={styles.modalActions} style={{justifyContent:'flex-end'}}>
              <button className={classNames(styles.btn, styles.btnOutline)} onClick={()=>setConfirmPortalOpen(false)}>Annuler</button>
              <button className={classNames(styles.btn, styles.btnPrimary)} onClick={()=>{ setConfirmPortalOpen(false); onPortalChange?.(!publicPortal); }}>
                {publicPortal ? 'Rendre privée' : 'Rendre publique'}
              </button>
            </div>
          </div>
        </div>, document.body
      )}

      {tvWindowOpen && <Box width="fit-content" onClose={()=>setTvWindowOpen(false)} marginTop="0px" contentPadding="0px"><div style={{aspectRatio:'16/9',height:'95vh',overflow:'hidden',borderRadius:8}}><ClonedVideoPlayer mediaStream={hlsMonitorStream}/></div></Box>}
      {/* ── Modale e-mail ── */}
      {shareModal === 'mail' && (
        <SendChannelUrlModal
          channelName = {name}
          publicUrl   = {publicUrl}
          userId      = {authentication.admin ? authentication.admin.id : 0}
          onClose     = {closeShareModal}
        />
      )}

      {/* ── Modale QR seul ── */}
      {shareModal === 'qr-only' && ReactDOM.createPortal(
        <div className={styles.modalOverlay}>
          <div className={styles.modalBox}>
            <div className={styles.modalHead}>
              <span className={styles.modalTitle}>QR code de la chaîne</span>
              <button className={styles.modalClose} onClick={closeShareModal}>×</button>
            </div>
            <div className={styles.modalUrl}>{publicUrl}</div>
            <div className={styles.qrCenter}>
              <QRCodeCanvas ref={qrOnlyRef} value={publicUrl} size={220} bgColor="#ffffff" fgColor="#000000" />
            </div>
            <div className={styles.modalActions} style={{justifyContent:'flex-end'}}>
              <button className={classNames(styles.btn, styles.btnOutline)} onClick={closeShareModal}>Fermer</button>
              <button className={classNames(styles.btn, styles.btnPrimary)} onClick={downloadQrOnly}>Télécharger en PNG</button>
            </div>
          </div>
        </div>, document.body
      )}

      {/* ── Modale QR dans image ── */}
      {shareModal === 'qr-image' && ReactDOM.createPortal(
        <div className={styles.modalOverlay}>
          <div className={styles.modalBox} style={{width: qrBgImageUrl ? 600 : 440}}>
            <div className={styles.modalHead}>
              <span className={styles.modalTitle}>Panneau QR code</span>
              <button className={styles.modalClose} onClick={closeShareModal}>×</button>
            </div>

            {!qrBgImageUrl ? (
              <label className={styles.qrUploadArea}>
                <Ico.Img/>
                <span>Cliquez pour choisir une image</span>
                <input type="file" accept="image/*" style={{display:'none'}} onChange={onBgImageFile}/>
              </label>
            ) : (
              <>
                <div
                  ref       = {imgContainerRef}
                  className = {styles.qrImgContainer}
                  onMouseMove = {onContainerMouseMove}
                  onMouseUp   = {onContainerMouseUp}
                  onMouseLeave= {onContainerMouseUp}
                >
                  <img ref={bgImgRef} src={qrBgImageUrl} alt="" className={styles.qrBgImg} draggable={false} onLoad={onBgImageLoad}/>
                  <div
                    className = {styles.qrOverlay}
                    style     = {{ left: qrPos.x, top: qrPos.y, width: qrSize, height: qrSize }}
                    onMouseDown={onQrMouseDown}
                  >
                    <QRCodeSVG value={publicUrl} size={qrSize} bgColor="#ffffff" fgColor="#000000"/>
                  </div>
                  <div
                    onMouseDown={onResizeMouseDown}
                    style={{
                      position: 'absolute',
                      left: qrPos.x + qrSize - 7,
                      top:  qrPos.y + qrSize - 7,
                      width: 14, height: 14,
                      cursor: 'nwse-resize',
                      background: 'rgba(0,0,0,0.65)',
                      borderRadius: 3,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 10,
                    }}
                  >
                    <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                      <path d="M7 1L1 7M7 4L4 7M7 7H4" stroke="white" strokeWidth="1.3" strokeLinecap="round"/>
                    </svg>
                  </div>
                </div>

                <div className={styles.modalActions} style={{justifyContent:'space-between'}}>
                  <label className={classNames(styles.btn, styles.btnOutline)} style={{cursor:'pointer'}}>
                    Changer l'image
                    <input type="file" accept="image/*" style={{display:'none'}} onChange={onBgImageFile}/>
                  </label>
                  <div style={{display:'flex', gap:8}}>
                    <button className={classNames(styles.btn, styles.btnOutline)} onClick={closeShareModal}>Fermer</button>
                    <button className={classNames(styles.btn, styles.btnPrimary)} onClick={downloadQrInImage}>Télécharger en PNG</button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>, document.body
      )}

{/* ── Header ── */}
<div className={styles.header}>
  <div>
    <div className={styles.titleRow}>
      <h1 className={styles.channelName}>{name||'Ma chaîne'}</h1>

      {/* Catégorie */}
      {categoryLabel && (
        <span className={styles.meta}>
          <Ico.Grid/><span className={styles.metaK}>Catégorie</span>
          <span className={styles.metaV}>{categoryLabel}</span>
        </span>
      )}

      {/* Accès */}
      {accessLabel && (
        <span className={styles.meta}>
          <Ico.Lock/><span className={styles.metaK}>Accès</span>
          <span className={styles.metaV}>{accessLabel}</span>
        </span>
      )}

      {/* Chaîne créée et pilotée par un autre système (BroadcastingChannel.ephemeral) */}
      {ephemeral && (
        <span className={styles.meta} title="Chaîne créée et pilotée par un autre système">
          <Ico.Clock/><span className={styles.metaV}>Éphémère</span>
        </span>
      )}
    </div>
    <p className={styles.tagline}>Diffusez. Partagez. Inspirez.</p>

    {/* Badges actif/inactif + portail public, côte à côte */}
    <div className={styles.badgeRow}>
      {channelStatus === 'active' && (
        <button className={classNames(styles.activeBadge, styles.activeBadgeOn)} onClick={()=>setConfirmToggleOpen(true)}>
          <span className={styles.dot}/>Chaîne active
        </button>
      )}
      {channelStatus === 'activating' && (
        <button className={classNames(styles.activeBadge, styles.activeBadgeActivating)} onClick={()=>setConfirmToggleOpen(true)}>
          <span className={classNames(styles.dot, styles.dotSpin)}/>Activation en cours...
        </button>
      )}
      {channelStatus === 'activating_restricted' && (
        <button className={classNames(styles.activeBadge, styles.activeBadgeActivatingRestricted)} title="Provisionnement pour enregistrement privé — la chaîne restera invisible du public" onClick={()=>setConfirmToggleOpen(true)}>
          <span className={classNames(styles.dot, styles.dotSpin)}/>Préparation de l'enregistrement...
        </button>
      )}
      {channelStatus === 'deactivating' && (
        <button className={classNames(styles.activeBadge, styles.activeBadgeDeactivating)} onClick={()=>setConfirmToggleOpen(true)}>
          <span className={classNames(styles.dot, styles.dotSpin)}/>Désactivation en cours...
        </button>
      )}
      {channelStatus === 'deactivating_restricted' && (
        <button className={classNames(styles.activeBadge, styles.activeBadgeDeactivatingRestricted)} title="Démontage de l'infra d'enregistrement privé — la chaîne redevient inactive" onClick={()=>setConfirmToggleOpen(true)}>
          <span className={classNames(styles.dot, styles.dotSpin)}/>Fin de l'enregistrement...
        </button>
      )}
      {channelStatus === 'inactive' && (
        <button className={classNames(styles.activeBadge, styles.activeBadgeOff)} onClick={()=>setConfirmToggleOpen(true)}>
          <span className={styles.dot}/>Chaîne inactive
        </button>
      )}
      {channelStatus === 'restricted' && (
        <button className={classNames(styles.activeBadge, styles.activeBadgeRestricted)} title="Enregistrement en cours — infra provisionnée, mais la chaîne reste invisible du public" onClick={()=>setConfirmToggleOpen(true)}>
          <span className={styles.dot}/>Enregistrement privé (chaîne non publique)
        </button>
      )}
      {channelStatus === 'error' && (
        <button className={classNames(styles.activeBadge, styles.activeBadgeError)} onClick={()=>setConfirmToggleOpen(true)}>
          <span className={classNames(styles.dot, styles.dotPulse)}/>Erreur — chaîne arrêtée
        </button>
      )}
      {channelStatus === 'recovering' && (
        <button className={classNames(styles.activeBadge, styles.activeBadgeRecovering)} onClick={()=>setConfirmToggleOpen(true)}>
          <span className={classNames(styles.dot, styles.dotPulse)}/>Signal perdu — tentative de reconnexion...
        </button>
      )}

      <button className={classNames(styles.activeBadge, publicPortal?styles.activeBadgeOn:styles.activeBadgeOff)} onClick={()=>setConfirmPortalOpen(true)}>
        <span className={styles.dot}/>{publicPortal?'Visible sur le portail public':'Invisible sur le portail public'}
      </button>

      {/* Résolution et paramètres vidéo du programme (profil de l'hôte + sens de la chaîne). */}
      {broadcastingChannelToEdit?.videoProfile && (() => {
        const v = broadcastingChannelToEdit.videoProfile!;
        const mbps = v.videoBitrateKbps >= 1000 ? `${(v.videoBitrateKbps / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mb/s` : `${v.videoBitrateKbps} kb/s`;
        return (
          <span className={classNames(styles.activeBadge, styles.videoBadge)}
                title={`Profil « ${v.name} » : ${v.width}×${v.height}, ${v.fps} images/s, ${v.codec}, débit vidéo maximal ${v.videoBitrateKbps} kb/s, audio AAC ${v.audioBitrateKbps} kb/s`}>
            <strong>{v.width}×{v.height}</strong>
            <span>{v.fps} i/s</span>
            <span>{v.codec}</span>
            <span>{mbps} max</span>
          </span>
        );
      })()}

      {/*
      <button
        className = {classNames(styles.activeBadge, metricsReachable && metricsEnabled ? styles.portalBadgeOn : styles.portalBadgeOff)}
        disabled  = {!metricsReachable}
        title     = {metricsReachable ? '' : "Chaîne inactive — système de mesure du ffmpeg-microservice indisponible"}
        onClick   = {()=>onMetricsToggle?.(!metricsEnabled)}
      >
        <span className={styles.dot}/>
        {!metricsReachable ? 'Mesure indisponible' : (metricsEnabled ? 'Mesure activée' : 'Mesure désactivée')}
      </button>
      */}

      {/*channelStatus === 'active' && typeof xtreamViewerCount === 'number' && (
        <span className={classNames(styles.activeBadge, styles.activeBadgeOn)} title="Connexions Xtream actives (fenêtre glissante)">
          <Ico.Eye/>{xtreamViewerCount} {xtreamViewerCount > 1 ? 'viewers' : 'viewer'}
        </span>
      )*/}
    </div>
  </div>

  {/* Url et bouton de partage — à droite, même niveau que le nom de la chaîne */}
  <div className={styles.headerRight}>
    <a className={styles.headerUrl} href={publicUrl} target="_blank" rel="noreferrer">
      <Ico.Link/>{publicUrl.replace(/^https?:\/\//,'')}<Ico.ExtLink/>
    </a>
    <div ref={shareMenuRef} style={{position:'relative'}}>
      <button className={classNames(styles.btn,styles.btnPrimary)} onClick={onShareUrl}><Ico.Share/>Partager le lien</button>
      {shareMenuOpen && (
        <div className={styles.shareDropdown}>
          <button className={styles.shareDropdownItem} onClick={openMailModal}>
            <Ico.Share/>Envoyer un mail
          </button>
          <button className={styles.shareDropdownItem} onClick={() => { setShareMenuOpen(false); setShareModal('qr-only'); }}>
            <Ico.QR/>Générer un QR seul
          </button>
          <button className={styles.shareDropdownItem} onClick={() => { setShareMenuOpen(false); setShareModal('qr-image'); setQrBgImageUrl(null); }}>
            <Ico.Img/>Générer un panneau QR code
          </button>
        </div>
      )}
    </div>
    {onCreateChannel && (
      <button className={classNames(styles.btn,styles.btnPrimary)} onClick={onCreateChannel}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
          <line x1="12" y1="5" x2="12" y2="19"/>
          <line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Créer une chaîne
      </button>
    )}
  </div>
</div>


      {/* ── Grille principale ── */}
      <div className={styles.mainGrid}>

        {/* col1 — aperçu */}
        <div className={styles.previewCol}>
          <div className={styles.card}>
            <div className={styles.cardHead}><Ico.Eye/><span>Aperçu public</span>{active&&<span className={styles.livePill}>● EN DIRECT</span>}</div>
            <div className={styles.playerWithAudio}>
              <div className={styles.playerBox}>
                <div className={styles.playerVideo} onClick={()=>setTvWindowOpen(true)}>
                  <MonitorVideoPlayer mediaStream={hlsMonitorStream}
                                      onClick={active && hlsMonitorStream?.active ? () => setTvWindowOpen(true) : undefined}
									  logo={logoSrc||undefined}
									  active={active} />
                </div>
                <button className={styles.resetBtn} onClick={()=>onHlsReset?.()} title="Réinitialiser HLS"><RefreshCw style={{width:11,height:11}}/></button>
              </div>
              <div className={styles.audioRow}>
                <HlsAudioPanel audioSourceRegistry={hlsAudioRegistry} audioSourceId={hlsAudioSourceId} externalVolume={hlsVolume} externalMuted={hlsMuted} onVolumeChange={onHlsVolumeChange} onMutedChange={onHlsMutedChange}/>
              </div>
            </div>
            <SourceSelector locked={channelStatus !== 'active' && channelStatus !== 'restricted' && channelStatus !== 'recovering' && channelStatus !== 'error'} streamSource={streamSource} defaultSource={defaultStreamSource} onStreamSourceChange={s=>onStreamSourceChange?.(s)} onDefaultStreamSourceChange={s=>onDefaultStreamSourceChange?.(s)} radioGroupName="srcDashboard" fontSize="13px"/>
            <div className={styles.metas}>
              {kwLabel && (
                <span className={styles.meta}>
                  <Ico.Tag/><span className={styles.metaK}>Mots-clés</span>
                  <span className={styles.metaV}>{kwLabel}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* col2 row1 — 4 stats */}
        <div className={styles.statsRow}>
          {channelStatus === 'active'
            ? <StatCard accent="#297332" accent2="#20413A" icon={<Icon name="radioSignal" color="#FFFFFF"/>} label="En ligne"  value={<span style={{color:'#2ecc71',fontSize:'0.78rem'}}>Votre chaîne est visible en direct</span>}/>
            : <StatCard accent="#FF0000" accent2="#7A2A2A" icon={<Icon name="radioSignal" color="#FFFFFF"/>} label="Hors ligne" value={<span style={{color:'#DC2626',fontSize:'0.78rem'}}>Votre chaîne n'est pas visible en direct</span>}/>}
          <StatCard accent="#314EA6" accent2="#213464" icon={<Icon name="groupOfPeople" color="#FFFFFF"/>} label="Spectateurs en direct" value={<span style={{fontSize:'1.4rem',fontWeight:700}}>{totalConnections}</span>}/>
          <StatCard accent="#4C2F87" accent2="#332B62" icon={<Icon name="clapboard"     color="#FFFFFF"/>} label="Vidéo en cours"        value={truncateTitle(currentMediaLabel)}/>
          <StatCard accent="#245A76" accent2="#1C3651" icon={<Icon name="antenna"       color="#FFFFFF"/>} label="Multidiffusion"        value={`${activeRestreams} plateforme${activeRestreams>1?'s':''}`}/>
        </div>

        {/* col2 row2 — programme + audience */}
        <div className={styles.bottomRow}>
          <div className={styles.card}>
            <div className={styles.cardHead}><Ico.Cal/><span>Programme du jour</span>
              <button className={styles.seeAll} onClick={()=>{ if(broadcastingChannelToEdit?.publicRef) fetchProgram(broadcastingChannelToEdit.publicRef); }}>Actualiser</button>
            </div>
            {renderProgram()}
          </div>
          <div className={styles.audienceCard}>
            <div className={styles.cardHead}><Ico.Users/><span>Audience en temps réel</span></div>
            <div className={styles.audienceCount}>
              <span className={styles.audienceNum}>{totalConnections}</span>
              <span className={styles.audienceLbl}>Connexions simultanées</span>
              {xtreamConnections > 0 && (
                <span className={styles.audienceLbl} style={{display:'block',fontSize:'0.7rem',opacity:0.75}}>
                  dont {xtreamConnections} IPTV (Xtream) <span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:'#3b82f6',marginLeft:4,verticalAlign:'middle'}}/>
                </span>
              )}
            </div>
            <div className={styles.audienceMap} style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={refreshAudience}
                title={audienceUpdatedAt ? `Actualiser la carte — relevé de ${audienceUpdatedAt.toLocaleTimeString('fr-FR')}` : 'Actualiser la carte'}
                style={{ position: 'absolute', top: 8, right: 8, zIndex: 7000, width: 26, height: 26, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                         border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.45)', color: '#ffffff', padding: 0 }}
              ><RefreshCw style={{ width: 13, height: 13 }} /></button>
              <ExtendablePanel logoUrl="/images/logo.png" logoPosition="bottomLeft">
              <OpenStreetMapV2
                coordinates={mapCoordinates}
                initialZoom={2}
                renderMode="dark"
                mapColors={{
                  water:      '#1b2947',
                  land:       '#263d6e',
                  landcover:  '#263d6e',
                  road:       '#2a2a4a',
                  building:   '#222244',
                  labelText:  '#c8d6e5',
                  labelHalo:  '#0d1b2a',
                }}
                cityLabelsOnly
              />
            </ExtendablePanel></div>
          </div>
        </div>

      </div>

      {/* ── Actions rapides + Assistant ── */}
      <div className={styles.row3}>
	  
<div className={styles.card}>
  <div className={styles.cardHead}><Ico.Play/><span>Actions rapides</span></div>
  <div className={styles.actionsGrid}>
    {[
      {bg:'#332967', border:'#3C3270', iconBg:'#922FBB', icon:<Icon name="videoPlaylist" color="#FFFFFF"/>, tab:2, newVideo:true, title:'Ajouter une vidéo',    desc:'Importez et organisez vos contenus'},
      {bg:'#253E76', border:'#2D4887', iconBg:'#2951C1', icon:<Icon name="videoCamera"   color="#FFFFFF"/>, tab:2, title:'Programmer un direct', desc:'Planifiez votre prochain événement'},
      {bg:'#372869', border:'#453377', iconBg:'#592FBA', icon:<Icon name="imageFile"     color="#FFFFFF"/>, tab:1, title:"Image d'attente",      desc:"Personnalisez l'écran d'attente"},
      {bg:'#621A2C', border:'#781F33', iconBg:'#D01B18', icon:<Icon name="youtubeIcon"   color="#FFFFFF"/>, tab:4, focus:'youtube' as const, title:'Configurer YouTube',   desc:'Diffusez aussi sur YouTube'},
    ].map((a,i)=>(
      <button
        key={i}
        className={styles.actionCard}
        style={{ background: a.bg, borderColor: a.border }}
        onClick={() => { a.newVideo ? openNewVideoSheet() : onNavigateTab?.(a.tab, a.focus); scrollPageToTop(); }}
        disabled={!onNavigateTab}
      >
        <div className={styles.actionIcon} style={{ background: a.iconBg }}>{a.icon}</div>
        <div className={styles.actionTitle}>{a.title}</div>
        <div className={styles.actionDesc}>{a.desc}</div>
      </button>
    ))}
  </div>
</div>

        <div className={classNames(styles.card, styles.assistantCard)}>
          <div className={styles.cardHead}><Ico.Star/><span>Assistant de diffusion</span></div>
          <div className={styles.assistantInner}>
            <div className={styles.setupLeft}>
              <span className={styles.assistantBadge}>{doneCount} / {steps.length} complétés</span>
              <div className={styles.setupList}>
                {steps.map((s, i) => <SetupStep key={i} label={s.label} done={s.done}/>)}
              </div>
            </div>
            <div className={styles.rocketImg}>
              <img src="/images/rocket.png" alt="Fusée"/>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default BroadcastingChannelDashboard;